/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash/fp';
import { load as loadWebFonts } from 'webfontloader';
import styled from '@emotion/styled';
import { padding } from 'polished';

import { GOLF } from 'app/constants/challengeOneSportIds';
import Spinner from 'app/components/commons/Spinner';
import AnimateNumber from 'app/components/commons/AnimateNumber';
import t from 'app/utils/translate';
import lineupEntitySelector from 'app/selectors/lineupEntitySelector';
import oneScoreImage from 'app/images/onesport/onescore.png';
import {
  fetchPlayerCompetitionData,
  fetchPlayerGameData,
  fetchPlayerSeasonData,
  fetchTeamCompetitionData,
  fetchTeamGameData,
  fetchTeamGameDataThrottle,
  fetchPlayerGameDataThrottle
} from '../../api/onesport';
import { LIVE_CHALLENGE_POLL_DURATION } from '../../constants/pollDurations';
import decodePlayerGameStats from '../../normalizers/decodePlayerGameStats';
import decodePlayerCompetitionStats from '../../normalizers/decodePlayerCompetitionStats';
import decodePlayerSeasonStats from '../../normalizers/decodePlayerSeasonStats';
import decodeTeamCompetitionStats from '../../normalizers/decodeTeamCompetitionStats';
import decodeTeamGameStats from '../../normalizers/decodeTeamGameStats';
import QuickStats from './components/QuickStats';
import { getAverage, getTrend } from './util';

type Props = {
  competitionId?: string,
  displayLogo: boolean,
  enablePolling: boolean,
  entityId?: string,
  gameId?: string,
  isCompressed?: boolean,
  gamePlaced?: boolean,
  isGolf?: boolean,
  isRacing?: boolean,
  isOpen: boolean,
  isTeam: boolean,
  sportId?: string,
  win?: number,
  place?: number,
  throttle?: boolean
};

type State = {
  isLoadingLiveGame: false,
  isLoadingPlayer: false,
  liveGameError: ?string,
  player: *,
  playerError: ?string,
  pollingEnabled: boolean,
  statistics: Array<*>
};

loadWebFonts({
  google: {
    families: ['Roboto']
  }
});

const Wrapper = styled.div(
  ({ theme }) => ({
    alignItems: 'stretch',
    backgroundColor: theme.color.white,
    borderBottom: `1px solid ${theme.color.basis6}`,
    color: 'black',
    display: 'flex',
    fontFamily: 'Roboto',
    fontSize: '0.65rem'
  }),
  ({ isLoading, theme }) =>
    isLoading
      ? {
          backgroundColor: theme.color.white,
          ...padding(theme.spacing.medium, null)
        }
      : {}
);

const Placeholder = styled.div(({ theme }) => ({
  fontSize: theme.fontSize.xxsmall,
  padding: theme.spacing.medium
}));

const StatsWrapper = styled.div({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1
});

const TableWrapper = styled.div({
  flexBasis: 0,
  flexGrow: 2,
  flexShrink: 2
});

const Table = styled.table({
  width: '100%'
});

const TableHeadRow = styled.tr({});

const TableHeading = styled.th(
  {
    color: '#333',
    fontWeight: 'inherit',
    padding: '0.2em 0.75em'
  },
  ({ initial }) =>
    initial
      ? {
          textAlign: 'left'
        }
      : {
          textAlign: 'center',
          width: 80
        }
);

const TableBodyRow = styled.tr(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f2f2f2',
    borderColor: theme.color.white
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.color.white,
    borderColor: theme.color.basis6
  }
}));

const TableCell = styled.td(
  {
    padding: '0.5em 0.75em'
  },
  ({ initial }) =>
    initial
      ? {
          textAlign: 'left'
        }
      : {
          textAlign: 'center'
        }
);

const HeadingImage = styled('img')({
  height: 12,
  width: 'auto'
});

class LivePlayerWidget extends React.Component<Props, State> {
  state: State = {
    isLoadingLiveGame: true,
    isLoadingPlayer: true,
    liveGameError: null,
    player: {},
    playerError: null,
    statistics: []
  };

  static defaultProps = {
    gamePlaced: false,
    isCompressed: false,
    isGolf: false,
    isRacing: false,
    competitionId: undefined,
    sportId: undefined,
    entityId: undefined,
    gameId: undefined,
    win: undefined,
    place: undefined,
    throttle: false
  };

  componentDidMount() {
    const { isOpen } = this.props;

    if (isOpen) {
      this.handleFetch();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { enablePolling, isOpen } = this.props;

    if (!prevProps.isOpen && isOpen) {
      this.handleFetch();
    }

    if (!prevProps.enablePolling && enablePolling) {
      this.pollLiveUpdates();
    }

    if ((prevProps.enablePolling && !enablePolling) || (prevProps.isOpen && !isOpen)) {
      clearInterval(this.pollTimer);
    }
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer);
  }

  handleFetch() {
    this.fetchPlayer();
    this.fetchLiveGame();
  }

  fetchPlayer() {
    const { competitionId, entityId, isCompressed, isGolf, isRacing, isTeam, sportId } = this.props;
    const decodeData = isTeam ? decodeTeamCompetitionStats : decodePlayerCompetitionStats;
    const fetchData = isTeam ? fetchTeamCompetitionData : fetchPlayerCompetitionData;

    if (!entityId || isRacing || isCompressed) {
      this.setState({
        isLoadingPlayer: false
      });
      return;
    }

    this.setState({
      isLoadingPlayer: true,
      playerError: null
    });

    if (!isGolf) {
      fetchData(sportId, entityId, competitionId)
        .then(res => {
          this.setState({
            player: decodeData(res.data),
            isLoadingPlayer: false
          });
        })
        .catch(() => {
          this.setState({
            playerError: 'Error fetching data',
            isLoadingPlayer: false
          });
        });
    } else if (!isTeam) {
      fetchPlayerSeasonData(sportId, entityId, competitionId)
        .then(res => {
          this.setState({
            player: decodePlayerSeasonStats(res.data),
            isLoadingPlayer: false
          });
        })
        .catch(() => {
          this.setState({
            playerError: 'Error fetching data',
            isLoadingPlayer: false
          });
        });
    }
  }

  fetchLiveGame() {
    const { enablePolling, entityId, gameId, isRacing, isTeam, throttle, sportId } = this.props;
    const decodeData = isTeam ? decodeTeamGameStats : decodePlayerGameStats;
    let fetchData;

    if (isTeam) {
      fetchData = throttle ? fetchTeamGameDataThrottle : fetchTeamGameData;
    } else {
      fetchData = throttle ? fetchPlayerGameDataThrottle : fetchPlayerGameData;
    }

    if (!entityId || isRacing) {
      this.setState({
        isLoadingLiveGame: false
      });
      return;
    }

    this.setState({
      isLoadingLiveGame: true,
      liveGameError: null
    });

    fetchData(sportId, entityId, gameId)
      .then(res => {
        const { statistics } = decodeData(res.data);
        this.setState({
          statistics,
          isLoadingLiveGame: false
        });
        if (enablePolling) {
          this.pollLiveUpdates();
        }
      })
      .catch(() => {
        this.setState({
          liveGameError: 'Error fetching game data :(',
          isLoadingLiveGame: false
        });
      });
  }

  pollLiveUpdates() {
    clearInterval(this.pollTimer);
    this.pollTimer = setInterval(() => {
      this.fetchLiveGame();
      this.setState(state => ({
        pollCount: state.pollCount + 1
      }));
    }, LIVE_CHALLENGE_POLL_DURATION);
  }

  renderRacingWidget() {
    const { displayLogo, gamePlaced, win, place } = this.props;

    if (gamePlaced && !place) {
      return <Placeholder>Did not place</Placeholder>;
    }

    return (
      <Table>
        <thead>
          <TableHeadRow>
            <TableHeading initial>
              {displayLogo && <HeadingImage src={oneScoreImage} alt="OneScore" />}
            </TableHeading>
            <TableHeading>{t('Pts')}</TableHeading>
          </TableHeadRow>
        </thead>
        <tbody>
          {(win > 0 || !gamePlaced) && (
            <TableBodyRow key="win">
              <TableCell initial>WIN</TableCell>
              <TableCell>
                <AnimateNumber format="0.00" number={win || 0} />
              </TableCell>
            </TableBodyRow>
          )}
          <TableBodyRow key="place">
            <TableCell initial>PLACE</TableCell>
            <TableCell>
              <AnimateNumber format="0.00" number={place || 0} />
            </TableCell>
          </TableBodyRow>
        </tbody>
      </Table>
    );
  }

  render() {
    const { displayLogo, isCompressed, isRacing, isOpen } = this.props;
    const {
      isLoadingLiveGame,
      isLoadingPlayer,
      liveGameError,
      player,
      playerError,
      statistics
    } = this.state;

    if (!isOpen) {
      return null;
    }

    if ((isLoadingLiveGame && statistics.length === 0) || (isLoadingPlayer && isEmpty(player))) {
      return (
        <Wrapper isLoading>
          <Spinner center kind="dark" size="small" />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        {!isCompressed && !isRacing && (
          <StatsWrapper>
            {!playerError && (
              <QuickStats
                layout="vertical"
                averageScore={player.averageScore}
                highScore={player.highScore}
                maxScore={player.maxScore}
                mediumTermAverage={getAverage(player.games, 6)}
                mediumTermTrend={getTrend(player.games, 6)}
                shortTermAverage={getAverage(player.games, 3)}
                shortTermTrend={getTrend(player.games, 3)}
              />
            )}
          </StatsWrapper>
        )}
        <TableWrapper>
          {isRacing && this.renderRacingWidget()}
          {(liveGameError || statistics.length === 0) && !isRacing && (
            <Placeholder>No player stats available</Placeholder>
          )}
          {statistics.length > 0 && !liveGameError && !isRacing && (
            <Table>
              <thead>
                <TableHeadRow>
                  <TableHeading initial>
                    {displayLogo && <HeadingImage src={oneScoreImage} alt="OneScore" />}
                  </TableHeading>
                  <TableHeading>{t('Qty')}</TableHeading>
                  <TableHeading>{t('Pts')}</TableHeading>
                </TableHeadRow>
              </thead>
              <tbody>
                {statistics.map(stat => (
                  <TableBodyRow key={stat.id}>
                    <TableCell initial>{stat.name}</TableCell>
                    <TableCell>{stat.quantity}</TableCell>
                    <TableCell>{stat.score || 0}</TableCell>
                  </TableBodyRow>
                ))}
              </tbody>
            </Table>
          )}
        </TableWrapper>
      </Wrapper>
    );
  }
}

const connector = connect((state, { id, isTeam, lineupId }) => {
  let entity = {};

  if (lineupId && get('myTeams.lineups', state)) {
    entity = lineupEntitySelector(state, { lineupId, id, isTeam });
  } else {
    const item = isTeam ? state.challenge.teams[id] : state.challenge.players[id];
    entity = { ...item, onesportSportId: get('challenge.onesportSportId', state) };
  }

  return {
    competitionId: get('onesportCompetitionId', entity),
    displayLogo: state.settings.onesport,
    entityId: get('onesportEntityId', entity),
    gameId: get('onesportGameId', entity),
    isGolf: get('onesportSportId', entity) === GOLF,
    sportId: get('onesportSportId', entity)
  };
});

export default connector(LivePlayerWidget);
